import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import type { Match } from '@/lib/site';

interface LiveModalProps {
  match: Match;
  onClose: () => void;
}

export default function LiveModal({ match, onClose }: LiveModalProps) {
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [onClose]);

  const isLive = match.status === 'live';
  const isUpcoming = match.status === 'upcoming';

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center px-4" role="dialog" aria-modal="true" aria-labelledby="live-modal-title">
      <button
        type="button"
        aria-label="关闭弹窗"
        onClick={onClose}
        className="absolute inset-0 bg-foreground-950/60 backdrop-blur-[2px] cursor-pointer"
      />
      <div className="relative w-full max-w-md rounded-lg bg-background-50 p-5 md:p-6 animate-pop-in">
        <button
          type="button"
          onClick={onClose}
          aria-label="关闭"
          className="absolute right-3 top-3 w-8 h-8 rounded-full bg-background-100 text-foreground-700 flex items-center justify-center hover:bg-background-200 transition-colors cursor-pointer"
        >
          <i className="ri-close-line" />
        </button>

        <span className="w-11 h-11 rounded-md bg-accent-100 text-accent-700 flex items-center justify-center text-xl">
          <i className={isUpcoming ? 'ri-notification-3-line' : 'ri-live-line'} />
        </span>

        <h3 id="live-modal-title" className="mt-3 font-heading text-lg text-foreground-950">
          {isUpcoming ? '赛前提醒' : '直播功能说明'}
        </h3>
        <p className="mt-1 text-xs text-foreground-500">
          {match.home.name} vs {match.away.name} · {match.timeLabel}
        </p>

        <div className="mt-4 rounded-md bg-background-100 border border-background-200 px-3.5 py-3">
          <p className="text-sm leading-relaxed text-foreground-700">
            {isLive
              ? '本场赛事目前显示为“直播中”，但本站尚未接入真实的流媒体直播服务，因此暂时无法播放比赛画面。'
              : isUpcoming
                ? '本场赛事尚未开始，预约与提醒功能仍在建设中。开赛后可回到本页查看赛况演示。'
                : '本场比赛已经结束，回放视频尚未接入真实视频源，当前仅可查看赛况数据与文字摘要。'}
          </p>
          <p className="mt-2 text-xs leading-relaxed text-foreground-500">
            页面中的比分、分节得分与命中率为前端演示数据，请以官方转播与官方数据为准。
          </p>
        </div>

        <div className="mt-4 flex flex-col sm:flex-row gap-2">
          <button
            type="button"
            onClick={onClose}
            className="flex-1 rounded-md bg-primary-900 px-4 py-2.5 text-sm text-background-50 hover:bg-primary-700 transition-colors cursor-pointer whitespace-nowrap"
          >
            我知道了
          </button>
          <Link
            to={`/category/${match.league}`}
            onClick={onClose}
            className="flex-1 inline-flex items-center justify-center gap-1.5 rounded-md border border-background-300 px-4 py-2.5 text-sm text-foreground-800 hover:border-primary-300 transition-colors whitespace-nowrap"
          >
            查看其他赛事
            <i className="ri-arrow-right-line" />
          </Link>
        </div>
      </div>
    </div>
  );
}